import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { messages, type MessageKey } from './messages'
import type { Locale } from '@/types/api'
import { useUiStore } from '@/stores/ui-store'

const localeNames: Record<Locale, string> = { tr: 'Türkçe', en: 'English', fr: 'Français', es: 'Español', de: 'Deutsch' }

interface I18nValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: MessageKey) => string
  formatDate: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useUiStore((state) => state.locale)
  const setLocale = useUiStore((state) => state.setLocale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<I18nValue>(() => ({
    locale,
    setLocale,
    t: (key) => messages[locale][key],
    formatDate: (date, options) => new Intl.DateTimeFormat(locale, options ?? { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(date)),
    formatNumber: (number, options) => new Intl.NumberFormat(locale, options).format(number),
  }), [locale, setLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n must be used inside I18nProvider')
  return context
}

export const supportedLocales = Object.keys(localeNames) as Locale[]
export function localeLabel(locale: Locale) { return localeNames[locale] }
